import React from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Avatar,
  useTheme,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  PhoneOutgoing,
  Users,
  Clock,
  CheckCircle2,
  FileText,
  DollarSign,
} from "lucide-react";

// Reusable Metric Card Component
const MetricCard = ({ icon: Icon, title, value, color, subtitle }) => {
  const theme = useTheme();

  return (
    <motion.div 
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
    >
      <Card
        sx={{
          borderRadius: 2,
          p: 2.5,
          height: "100%",
          minHeight: 140,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          boxShadow: "0px 1px 3px rgba(0,0,0,0.05)",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
            borderColor: alpha(theme.palette[color].main, 0.2),
          },
        }}
      >
        {/* Top Bar */}
        <Box
          sx={{
            height: 3,
            borderRadius: "3px",
            background: `linear-gradient(90deg, ${alpha(
              theme.palette[color].main,
              0.5
            )}, ${theme.palette[color].main})`,
            mb: 2,
          }}
        />

        {/* Icon and Title Row */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: alpha(theme.palette[color].main, 0.12),
              color: theme.palette[color].main,
              width: 44,
              height: 44,
            }}
          >
            <Icon size={22} strokeWidth={2} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ 
                color: "text.secondary", 
                textTransform: "uppercase",
                letterSpacing: 0.5,
                fontWeight: 600,
                display: "block",
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{ 
                  color: alpha(theme.palette.text.secondary, 0.6),
                  fontSize: "0.7rem",
                  display: "block",
                  mt: 0.25,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Value */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            lineHeight: 1,
            letterSpacing: -0.5,
          }}
        >
          {value}
        </Typography>
      </Card>
    </motion.div>
  );
};

// Main Component
const CustomerSnapshot = () => {
  const metrics = [
    {
      title: "Total Leads",
      value: "342",
      icon: Users,
      color: "success",
      subtitle: "Active Pipeline"
    },
    {
      title: "Total Calls",
      value: "1,204",
      icon: PhoneOutgoing,
      color: "primary",
      subtitle: "This Month"
    },
    {
      title: "Avg Talktime",
      value: "16 min",
      icon: Clock,
      color: "info",
      subtitle: "Daily Average"
    },
    {
      title: "Conversions",
      value: "24",
      icon: CheckCircle2,
      color: "success",
      subtitle: "This Month"
    },
    {
      title: "Invoices Due",
      value: "12",
      icon: FileText,
      color: "error",
      subtitle: "Pending Payment"
    },
    {
      title: "Revenue",
      value: "$48.2K",
      icon: DollarSign,
      color: "success",
      subtitle: "This Month"
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}>
          Customer Snapshot
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Overview of your key business metrics
        </Typography>
      </Box>

      {/* Metrics Grid */}
      <Grid container spacing={2.5}>
        {metrics.map((metric, index) => (
          <Grid key={index} item xs={12} sm={6} md={4} lg={2}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CustomerSnapshot;